import React, { useEffect, useState } from "react";
import { useLanguage } from "../../../Authentication/LanguageContext";
import ServerUrl from "../../../../BACKEND";
import "./A1Stories.css";

interface StoryItem {
  id: number;
  image: string;
  video: string;
  headergerman: string;
  headertamil: string;
  headerenglish: string;
  headersinhala: string;
  headerhindi: string;
  headerarabic: string;
  headerturkish: string;
  headerrussian: string;
  headerchinese: string;
  headerfrench: string;
  headerspanish: string;
  subheadergerman: string;
  subheadertamil: string;
  subheaderenglish: string;
  subheadersinhala: string;
  subheaderhindi: string;
  subheaderarabic: string;
  subheaderturkish: string;
  subheaderrussian: string;
  subheaderchinese: string;
  subheaderfrench: string;
  subheaderspanish: string;
  contentgerman: string;
  contenttamil: string;
  contentenglish: string;
  contentsinhala: string;
  contenthindi: string;
  contentarabic: string;
  contentturkish: string;
  contentrussian: string;
  contentchinese: string;
  contentfrench: string;
  contentspanish: string;
  [key: string]: string | number;
}

const capitalize = (text: string) => {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "";
};

const A1Stories: React.FC = () => {
  const [stories, setStories] = useState<StoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const { language } = useLanguage();

  const translations = {
    title: {
      de: "Geschichten", // Always show German title
    },
    subtitle: {
      en: "Practice German Stories",
      de: "Deutsche Geschichten üben",
      ta: "ஜெர்மன் கதைகளை பயிற்சி செய்யுங்கள்",
      si: "ජර්මානු කතාන්දර පුහුණු කරන්න",
      hi: "जर्मन कहानियों का अभ्यास करें",
      ar: "تدرب على القصص الألمانية",
      tr: "Almanca hikayeleri pratik yapın",
      ru: "Практика немецких историй",
      zh: "练习德语故事",
      fr: "Pratiquer des histoires en allemand",
      es: "Practica historias en alemán",
    },
  };

  const languageMap: Record<string, string> = {
    en: "english",
    de: "german",
    ta: "tamil",
    si: "sinhala",
    hi: "hindi",
    ar: "arabic",
    tr: "turkish",
    ru: "russian",
    zh: "chinese",
    fr: "french",
    es: "spanish",
  };

  useEffect(() => {
    fetch(`${ServerUrl}/api/stories`)
      .then((res) => res.json())
      .then((json) => setStories(json))
      .catch((error) => console.error("Error loading stories:", error))
      .finally(() => setLoading(false));
  }, [language]);

  const speakText = (text: string) => {
    if (currentlyPlaying === text) {
      setCurrentlyPlaying(null);
      speechSynthesis.cancel();
      return;
    }

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "de-DE";
    speechSynthesis.speak(utterance);
    setCurrentlyPlaying(text);

    utterance.onend = () => {
      setCurrentlyPlaying(null);
    };
  };

  const getTranslation = (item: StoryItem, fieldPrefix: string) => {
    const fieldName = `${fieldPrefix}${languageMap[language] || "german"}`;
    const value = item[fieldName];
    return typeof value === "string" ? capitalize(value) : "";
  };

  return (
    <div className="stories-page-container">
      <div className="stories-page-header">
        <h1 className="stories-page-title">{translations.title.de}</h1>
        <p className="stories-page-subtitle">{translations.subtitle[language]}</p>
      </div>

      {loading ? (
        <div className="stories-page-loading">Loading...</div>
      ) : (
        <div className="stories-page-scroll-container">
          {stories.map((story, index) => (
            <div key={story.id} className="stories-page-block-card">
              {/* Header */}
              <div className="stories-page-block-header-container">
                <h2 className="stories-page-block-header">
                  {`${index + 1}. ${story.headergerman}`}
                </h2>
                {getTranslation(story, "header") && (
                  <h3 className="stories-page-block-header-translated">
                    {getTranslation(story, "header")}
                  </h3>
                )}
              </div>

              {/* Subheader */}
              <div className="stories-page-subheader-container">
                <p className="stories-page-block-subheader">
                  {story.subheadergerman}
                </p>
                {getTranslation(story, "subheader") && (
                  <p className="stories-page-block-subheader-translated">
                    {getTranslation(story, "subheader")}
                  </p>
                )}
              </div>

              {/* Dialog Content */}
              <div className="stories-page-dialog-container">
                <div 
                  className={`stories-page-dialog-row ${currentlyPlaying === story.contentgerman ? "stories-page-playing" : ""}`}
                  onClick={() => speakText(story.contentgerman)}
                >
                  <div className="stories-page-dialog-content">
                    <p className="stories-page-dialog-text">
                      {story.contentgerman}
                    </p>
                    {getTranslation(story, "content") && (
                      <p className="stories-page-dialog-translation">
                        {getTranslation(story, "content")}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default A1Stories;
